'use client';

import { ReactNode, useState } from "react";
import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "sonner";

import { SupabaseProvider } from "@/components/providers/SupabaseProvider";
import { PosthogProvider } from "@/components/providers/PosthogProvider";
import { WebVitalsProvider } from "@/components/providers/WebVitalsProvider";

type AppProvidersProps = {
  children: ReactNode;
};

export function AppProviders({ children }: AppProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 30_000,
            // Garbage collect unused queries after 5 minutes to prevent memory buildup
            gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
            // Limit retries to prevent infinite loops
            retry: 2,
          },
          mutations: {
            // Garbage collect completed mutations after 5 minutes
            gcTime: 5 * 60 * 1000,
          },
        },
      }),
  );

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <SupabaseProvider>
        <PosthogProvider>
          <QueryClientProvider client={queryClient}>
            <WebVitalsProvider>
              {children}
            </WebVitalsProvider>
            <Toaster richColors position="top-right" />
          </QueryClientProvider>
        </PosthogProvider>
      </SupabaseProvider>
    </ThemeProvider>
  );
}
